import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import type { User } from '@supabase/supabase-js'
import { signIn, signOut, signUp } from '../services/supabase'

export type UserRole = 'customer' | 'business' | 'admin'

export interface UserProfile {
  id: string
  email: string
  full_name?: string
  phone?: string
  role: UserRole
  location?: {
    lat: number
    lng: number
    address?: string
  }
  preferences?: {
    preferred_brands?: string[]
    price_range?: [number, number]
    notification_settings?: {
      email: boolean
      sms: boolean
      push: boolean
    }
  }
  // Vehicle information fields
  vehicle_make?: string
  vehicle_model?: string
  vehicle_year?: number
  whatsapp_number?: string
  whatsapp_verified?: boolean
  marketing_consent?: boolean
  marketing_consent_at?: string
  created_at: string
  updated_at: string
}

interface PendingBookingData {
  customer_name: string
  customer_email: string
  customer_phone: string
  preferred_date: string
  preferred_time: string
  notes: string
  service_type: string
}

interface PendingAction {
  type: 'message' | 'booking'
  dealId?: string
  dealData?: any
  businessData?: any
  businessContext?: any
  formData?: any
  redirectTo?: string
  bookingData?: PendingBookingData
}

interface AuthState {
  user: User | null
  profile: UserProfile | null
  isAuthenticated: boolean
  isLoading: boolean
  error: string | null
  pendingAction: PendingAction | null
}

interface AuthActions {
  setUser: (user: User | null) => void
  setProfile: (profile: UserProfile | null) => void
  setLoading: (loading: boolean) => void
  setError: (error: string | null) => void
  setPendingAction: (action: PendingAction | null) => void
  clearPendingAction: () => void
  login: (email: string, password: string) => Promise<void>
  register: (email: string, password: string, userData?: Partial<UserProfile>) => Promise<'ok' | 'confirm_email'>
  logout: () => Promise<void>
  clearError: () => void
}

type AuthStore = AuthState & AuthActions

export const useAuthStore = create<AuthStore>()(
  persist(
    (set) => ({
      // State
      user: null,
      profile: null,
      isAuthenticated: false,
      isLoading: true,
      error: null,
      pendingAction: null,

      // Actions
      setPendingAction: (action) => {
        set({ pendingAction: action })
      },

      clearPendingAction: () => {
        set({ pendingAction: null })
      },

      setUser: async (user) => {
        if (user) {
          // Check if user is a business owner
          const supabase = await import('../services/supabase').then(m => m.supabase);
          
          console.log('[DEBUG] Setting user, checking business ownership for user:', user.id);
          
          const { data: businessData, error: businessError } = await supabase
            .from('businesses')
            .select('id,name,contact_email,verified,contact_phone')
            .eq('owner_id', user.id)
            .maybeSingle();

          console.log('[DEBUG] Business query result:', { businessData, businessError });

          let profile: any = null;
          
          if (businessData && !businessError) {
            // User is a business owner
            console.log('[DEBUG] User is business owner, creating business profile');
            profile = {
              id: user.id,
              email: user.email || '',
              role: 'business' as UserRole,
              full_name: (businessData as any)?.name || 'Business Owner',
              phone: (businessData as any)?.contact_phone || '',
              created_at: new Date().toISOString(),
              updated_at: new Date().toISOString()
            };
          } else {
            // Try to fetch regular user profile
            console.log('[DEBUG] No business found, checking user_profiles table');
            const { data: profileData, error: profileError } = await supabase
              .from('user_profiles')
              .select('*')
              .eq('id', user.id)
              .single();
            
            console.log('[DEBUG] User profile query result:', { profileData, profileError });
            
            if (profileData && !profileError) {
              profile = profileData;
            } else {
              // Create default customer profile
              console.log('[DEBUG] No profile found, creating default customer profile');
              profile = {
                id: user.id,
                email: user.email || '',
                role: 'customer',
                full_name: user.email?.split('@')[0] || 'User',
                phone: '',
                created_at: new Date().toISOString(),
                updated_at: new Date().toISOString()
              };
            }
          }

          console.log('[DEBUG] Final profile set:', profile);
          set({
            user,
            profile,
            isAuthenticated: true,
            isLoading: false,
          });
        } else {
          console.log('[DEBUG] No user, clearing auth state');
          set({
            user: null,
            profile: null,
            isAuthenticated: false,
            isLoading: false,
          });
        }
      },

      setProfile: (profile) => {
        set({ profile })
      },

      setLoading: (isLoading) => {
        set({ isLoading })
      },

      setError: (error) => {
        set({ error })
      },

      clearError: () => {
        set({ error: null })
      },

      login: async (email, password) => {
        try {
          set({ isLoading: true, error: null })
          // Debug log: credentials sent to Supabase
          console.log("[DEBUG] Supabase login payload:", { email, password })
          const { data, error } = await signIn(email, password)
          
          if (error) {
            set({ error: error.message, isLoading: false })
            return
          }

          if (data.user) {
            // Admin access is restricted to an explicit allow-list of emails.
            // Nobody else can be an admin, regardless of any DB role value.
            const ADMIN_EMAILS = ['suren@martech.st', 'hello@tyrelink.app'];
            const isAllowlistedAdmin = !!(data.user.email &&
              ADMIN_EMAILS.includes(data.user.email.toLowerCase()));

            let profile: UserProfile | null = null
            if (isAllowlistedAdmin) {
              profile = {
                id: data.user.id,
                email: data.user.email!,
                role: 'admin',
                full_name: 'TyreLink Admin',
                phone: '',
                created_at: new Date().toISOString(),
                updated_at: new Date().toISOString()
              }
            } else {
              // Real users: Check if they're a business owner first
              const supabase = await import('../services/supabase').then(m => m.supabase);
              
              console.log('[DEBUG] Login: checking business ownership for user:', data.user.id);
              
              // First check if user owns a business
              const { data: businessData, error: businessError } = await supabase
                .from('businesses')
                .select('id,name,contact_email,verified,contact_phone')
                .eq('owner_id', data.user.id)
                .maybeSingle();

              console.log('[DEBUG] Login business query:', { businessData, businessError });

              if (businessData && !businessError) {
                // User is a business owner
                console.log('[DEBUG] Login: User is business owner');
                profile = {
                  id: data.user.id,
                  email: data.user.email || '',
                  role: 'business' as UserRole,
                  full_name: (businessData as any)?.name || 'Business Owner',
                  phone: (businessData as any)?.contact_phone || '',
                  created_at: new Date().toISOString(),
                  updated_at: new Date().toISOString()
                };
              } else {
                // Try to fetch regular user profile
                console.log('[DEBUG] Login: No business, checking user_profiles');
                const { data: profileData, error: profileError } = await supabase
                  .from('user_profiles')
                  .select('*')
                  .eq('id', data.user.id)
                  .single();
                
                console.log('[DEBUG] Login user profile query:', { profileData, profileError });
                
                if (profileData && !profileError) {
                  profile = profileData as UserProfile;
                } else {
                  // Create default customer profile for users without explicit profile
                  console.log('[DEBUG] Login: Creating default customer profile');
                  profile = {
                    id: data.user.id,
                    email: data.user.email || '',
                    role: 'customer',
                    full_name: data.user.email?.split('@')[0] || 'User',
                    phone: '',
                    created_at: new Date().toISOString(),
                    updated_at: new Date().toISOString()
                  };
                }
              }
            }

            // Safety net: only allow-listed emails may EVER hold the admin role,
            // even if a stray user_profiles row says role = 'admin'.
            if (profile && profile.role === 'admin' && !isAllowlistedAdmin) {
              profile = { ...profile, role: 'customer' };
            }
            console.log('[DEBUG] login profile:', profile);
            set({
              user: data.user as User,
              profile,
              isAuthenticated: true,
              isLoading: false,
              error: null
            })
          }
        } catch (error) {
          const errorMessage = error instanceof Error ? error.message : 'Login failed'
          set({ error: errorMessage, isLoading: false })
        }
      },

      register: async (email, password, userData) => {
        set({ isLoading: true, error: null })

        const { data, error } = await signUp(email, password, {
          role: userData?.role || 'customer',
          full_name: userData?.full_name,
          phone: userData?.phone,
          vehicle_make: userData?.vehicle_make,
          vehicle_model: userData?.vehicle_model,
          vehicle_year: userData?.vehicle_year,
          marketing_consent: userData?.marketing_consent,
        })

        if (error) {
          set({ error: error.message, isLoading: false })
          throw new Error(error.message)
        }

        // Supabase quirk: when "Confirm email" is enabled and the email is ALREADY
        // registered, signUp returns a fake user with NO error and an empty
        // identities array. Detect it so we don't claim success.
        if (data.user && (!data.user.identities || data.user.identities.length === 0)) {
          const msg = 'This email is already registered. Please log in instead.'
          set({ error: msg, isLoading: false })
          throw new Error(msg)
        }

        // "Confirm email" enabled: user created but NO session yet — they are not
        // logged in until they click the link in their inbox.
        if (data.user && !data.session) {
          set({ user: null, isAuthenticated: false, isLoading: false, error: null })
          return 'confirm_email'
        }

        if (data.user && data.session) {
          // Save vehicle info to user_profiles table (session exists, so RLS passes)
          try {
            const supabase = await import('../services/supabase').then(m => m.supabase);
            await supabase
              .from('user_profiles')
              .upsert({
                id: data.user.id,
                email: email,
                role: userData?.role || 'customer',
                full_name: userData?.full_name,
                phone: userData?.phone,
                vehicle_make: userData?.vehicle_make || null,
                vehicle_model: userData?.vehicle_model || null,
                vehicle_year: userData?.vehicle_year || null,
                marketing_consent: userData?.marketing_consent ?? null,
                marketing_consent_at: userData?.marketing_consent !== undefined ? new Date().toISOString() : null,
                updated_at: new Date().toISOString()
              }, { onConflict: 'id' })
          } catch (profileErr) {
            console.error('Error saving vehicle info to profile:', profileErr)
            // Don't fail registration if profile upsert fails
          }

          set({
            user: data.user,
            isAuthenticated: true,
            isLoading: false,
            error: null
          })
          return 'ok'
        }

        const fallbackMsg = 'Registration failed. Please try again.'
        set({ error: fallbackMsg, isLoading: false })
        throw new Error(fallbackMsg)
      },

      logout: async () => {
        // Always clear local state first — don't let a failed API call keep the user "logged in"
        const clearLocalAuth = () => {
          set({
            user: null,
            profile: null,
            isAuthenticated: false,
            isLoading: false,
            error: null,
            pendingAction: null,
          })
          // Clear persisted zustand auth store
          localStorage.removeItem('tyrelink-auth-storage')
          // Clear Supabase's own session keys
          Object.keys(localStorage)
            .filter(k => k.startsWith('sb-') || k.startsWith('supabase'))
            .forEach(k => localStorage.removeItem(k))
          // Clear chat session so stale auth-flow messages don't replay
          localStorage.removeItem('tyrelink_chat_session')
          localStorage.removeItem('tyrelink_chat_session_id')
          localStorage.removeItem('tyrelink_chat_auth_flow')
        }

        clearLocalAuth() // Clear local state immediately to prevent UI glitches

        try {
          set({ isLoading: true })
          await signOut()
        } catch (err) {
          // signOut throwing (rare) should never block the local logout
          console.warn('[AuthStore] signOut threw (ignored — clearing local state anyway):', err)
          // Still flush Supabase in-memory state so it doesn't re-persist the session
          try {
            const { supabase: sb } = await import('../services/supabase')
            await sb.auth.signOut({ scope: 'local' })
          } catch (_) { /* ignore */ }
        } finally {
          clearLocalAuth()
        }
      },
    }),
    {
      name: 'tyrelink-auth-storage',
      partialize: (state) => ({
        user: state.user,
        profile: state.profile,
        isAuthenticated: state.isAuthenticated,
        pendingAction: state.pendingAction,
      }),
    }
  )
)

// Demo user helpers for MVP
// Demo/test logins removed for production (were: DEMO_USERS with hardcoded
// passwords, and loginDemoUser). Admin access is now restricted to an explicit
// email allow-list in the login handler above.
